import { useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useTier } from '../context/TierContext';

/**
 * useCompareCap - Centralizes the Free 2-listing comparison cap policy.
 *
 * This hook owns the *policy* only: tier check, count check, PostHog events,
 * and modal dispatch. State ownership (the current selection array + setter)
 * stays with the caller — that way both `ListingsFeedContext` (feed screen)
 * and `UnifiedFeedScreen` (unified feed) can share this logic without
 * fighting over who owns the selection.
 *
 * Usage:
 *   const { attemptCompareSelect, isSelecting } = useCompareCap();
 *   attemptCompareSelect(listing, currentSelection, {
 *     onSelect: (next) => setSelection(next),
 *     onClear:  () => setSelection([]),
 *   });
 *
 * @returns {{
 *   attemptCompareSelect: (listing, currentSelection, handlers) => void,
 *   isSelecting: (currentSelection: any[]) => boolean,
 * }}
 */
export const useCompareCap = () => {
  const { hasProAccess, triggerUpgradeModal } = useTier();
  const posthog = usePostHog();

  const attemptCompareSelect = useCallback(
    (listing, currentSelection = [], { onSelect, onClear } = {}) => {
      if (!listing) return;
      const current = Array.isArray(currentSelection) ? currentSelection : [];

      // Toggle: already in selection → remove it
      if (current.some((l) => l.id === listing.id)) {
        onSelect?.(current.filter((l) => l.id !== listing.id));
        return;
      }

      // Pro 2-cap (also applies to Free after the first entry — see below).
      if (current.length >= 2) return;

      if (!hasProAccess) {
        // Free users get one slot; the second attempt triggers the paywall.
        if (current.length === 0) {
          posthog?.capture('comparison_first_listing_selected');
          onSelect?.([listing]);
        } else {
          posthog?.capture('comparison_second_listing_attempted', {
            triggered_paywall: true,
          });
          triggerUpgradeModal({
            source: 'cap_compare',
            onDismiss: () => onClear?.(),
          });
        }
        return;
      }

      onSelect?.([...current, listing]);
    },
    [hasProAccess, triggerUpgradeModal, posthog]
  );

  /**
   * Whether the caller can still add a listing to the passed-in selection.
   * - Pro:  true while selection has room (< 2)
   * - Free: true only when selection is empty (1st slot unlocked, 2nd blocks)
   */
  const isSelecting = useCallback(
    (currentSelection = []) => {
      const len = Array.isArray(currentSelection) ? currentSelection.length : 0;
      if (len >= 2) return false;
      if (hasProAccess) return true;
      return len === 0;
    },
    [hasProAccess]
  );

  return { attemptCompareSelect, isSelecting };
};

export default useCompareCap;
